import { useEffect, useMemo, useState } from 'react';

export const SERVER_PHASE = {
  INACTIVE: 'inactive',
  BETREADY: 'betready',
  LOCKED: 'locked',
  RESULT_PENDING: 'resultPending',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
};

export const TIME_PHASE = {
  WAITING: 'waiting',
  FAST: 'fast',
  ACTIVE: 'active',
  ENDED: 'ended',
};

export const ROOM_DISPLAY_PHASE = {
  INACTIVE: 'inactive',
  BETREADY: 'betready',
  LOCKED: 'locked',
  RESULT_PENDING: 'resultPending',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  BET_TIME: 'betTime',
  BET_COMPLETION: 'betCompletion',
};

export const SERVER_STATUS_TO_PHASE = {
  0: SERVER_PHASE.INACTIVE,
  1: SERVER_PHASE.BETREADY,
  2: SERVER_PHASE.LOCKED,
  3: SERVER_PHASE.RESULT_PENDING,
  4: SERVER_PHASE.CONFIRMED,
  5: SERVER_PHASE.COMPLETED,
};

const BETTABLE_TIME_PHASES = new Set([TIME_PHASE.FAST, TIME_PHASE.ACTIVE]);

export const getServerPhaseFromStatus = (status) => {
  const numericStatus = Number(status);
  return SERVER_STATUS_TO_PHASE[numericStatus] ?? SERVER_PHASE.INACTIVE;
};

export const getServerPhaseFromSseData = (sseData) => {
  const payloadStatus = sseData?.payload?.status;
  if (payloadStatus !== undefined && payloadStatus !== null) {
    return getServerPhaseFromStatus(payloadStatus);
  }

  return null;
};

export const getServerPhaseCommandFromSseData = (sseData) => {
  if (
    sseData?.type === 'system' &&
    sseData.action === 'sync' &&
    sseData.target === 'roundStatus'
  ) {
    return getServerPhaseFromSseData(sseData);
  }

  return null;
};

export const getTimePhase = (round, now = new Date()) => {
  const starttime = round?.starttime ? new Date(round.starttime) : null;
  const fasttime = round?.fasttime ? new Date(round.fasttime) : null;
  const endtime = round?.endtime ? new Date(round.endtime) : null;

  if (!starttime || !endtime || Number.isNaN(starttime.getTime()) || Number.isNaN(endtime.getTime())) {
    return TIME_PHASE.WAITING;
  }

  if (now < starttime) {
    return TIME_PHASE.WAITING;
  }

  if (fasttime && !Number.isNaN(fasttime.getTime()) && now < fasttime) {
    return TIME_PHASE.FAST;
  }

  if (now < endtime) {
    return TIME_PHASE.ACTIVE;
  }

  return TIME_PHASE.ENDED;
};

const getNextTimePhaseChangeDelayMs = (round, now = new Date()) => {
  const timestamps = [round?.starttime, round?.fasttime, round?.endtime]
    .map((value) => (value ? new Date(value) : null))
    .filter((date) => date && !Number.isNaN(date.getTime()) && date.getTime() > now.getTime())
    .map((date) => date.getTime());

  if (timestamps.length === 0) {
    return null;
  }

  return Math.max(Math.min(...timestamps) - now.getTime() + 50, 0);
};

export const getTimeSignals = (round, now = new Date()) => {
  const starttime = round?.starttime ? new Date(round.starttime) : null;
  const fasttime = round?.fasttime ? new Date(round.fasttime) : null;
  const endtime = round?.endtime ? new Date(round.endtime) : null;
  const timePhase = getTimePhase(round, now);
  const remainingSeconds = endtime && !Number.isNaN(endtime.getTime())
    ? Math.max((endtime.getTime() - now.getTime()) / 1000, 0)
    : null;
  const totalSeconds = starttime && endtime && endtime > starttime
    ? Math.max((endtime.getTime() - starttime.getTime()) / 1000, 1)
    : null;
  const elapsedRatio = remainingSeconds !== null && totalSeconds
    ? Math.min(Math.max((totalSeconds - remainingSeconds) / totalSeconds, 0), 1)
    : null;
  const startCountdownSeconds = starttime && !Number.isNaN(starttime.getTime())
    ? Math.max((starttime.getTime() - now.getTime()) / 1000, 0)
    : null;
  const fastCountdownSeconds = fasttime && !Number.isNaN(fasttime.getTime())
    ? Math.max((fasttime.getTime() - now.getTime()) / 1000, 0)
    : null;
  const countdownSeconds = (() => {
    if (timePhase === TIME_PHASE.WAITING) {
      return startCountdownSeconds;
    }

    if (timePhase === TIME_PHASE.ENDED) {
      return remainingSeconds;
    }

    return remainingSeconds;
  })();
  const countdownTarget = (() => {
    if (timePhase === TIME_PHASE.WAITING) {
      return 'start';
    }

    if (timePhase === TIME_PHASE.ENDED) {
      return null;
    }

    return 'end';
  })();
  const countdownLabel = (() => {
    if (timePhase === TIME_PHASE.WAITING) {
      return '次ラウンドのベット開始まであと';
    }

    if (timePhase === TIME_PHASE.ENDED) {
      return 'ベット終了';
    }

    return 'ベット終了まであと';
  })();
  const showProgress = [TIME_PHASE.FAST, TIME_PHASE.ACTIVE].includes(timePhase) && elapsedRatio !== null;
  const progressRatio = showProgress ? 1 - elapsedRatio : null;

  return {
    timePhase,
    remainingSeconds,
    elapsedRatio,
    countdownSeconds,
    countdownTarget,
    countdownLabel,
    fastCountdownSeconds: timePhase === TIME_PHASE.FAST ? fastCountdownSeconds : null,
    progressRatio,
    showProgress,
    timerLevel: getTimerLevel(countdownSeconds, timePhase),
    isNearEnd: remainingSeconds !== null && remainingSeconds <= 10 && timePhase !== TIME_PHASE.ENDED,
  };
};

export const getTimerLevel = (remainingSeconds, timePhase) => {
  if (remainingSeconds === null || timePhase === TIME_PHASE.ENDED) {
    return 'normal';
  }

  if (remainingSeconds <= 3) {
    return 'critical';
  }

  if (remainingSeconds <= 10) {
    return 'warning';
  }

  return 'normal';
};

export const formatRemainingDuration = (seconds) => {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) {
    return '';
  }

  if (Number(seconds) < 60) {
    return `${Math.max(0, Number(seconds)).toFixed(1)} 秒`;
  }

  const totalSeconds = Math.max(0, Math.ceil(Number(seconds)));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const restSeconds = totalSeconds % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}時間`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}分`);
  }

  if (hours === 0 && (restSeconds > 0 || parts.length === 0)) {
    parts.push(`${restSeconds}秒`);
  }

  return parts.join('');
};

export const getRoomDisplayPhase = ({ serverPhase, timePhase, event, round, user }) => {
  if (serverPhase === SERVER_PHASE.COMPLETED) {
    return ROOM_DISPLAY_PHASE.COMPLETED;
  }

  if (serverPhase === SERVER_PHASE.CONFIRMED) {
    return ROOM_DISPLAY_PHASE.CONFIRMED;
  }

  if (serverPhase === SERVER_PHASE.RESULT_PENDING) {
    return ROOM_DISPLAY_PHASE.RESULT_PENDING;
  }

  if (serverPhase === SERVER_PHASE.LOCKED) {
    return ROOM_DISPLAY_PHASE.LOCKED;
  }

  const isManualBetClose = event?.bet_close_mode === 'manual';

  if (
    serverPhase === SERVER_PHASE.BETREADY &&
    (BETTABLE_TIME_PHASES.has(timePhase) || (isManualBetClose && timePhase === TIME_PHASE.ENDED))
  ) {
    return ROOM_DISPLAY_PHASE.BET_TIME;
  }

  if (serverPhase === SERVER_PHASE.BETREADY && timePhase === TIME_PHASE.ENDED) {
    return ROOM_DISPLAY_PHASE.BET_COMPLETION;
  }

  return ROOM_DISPLAY_PHASE[serverPhase.toUpperCase()] ?? ROOM_DISPLAY_PHASE.INACTIVE;
};

function useRoomPhase({
  event,
  phaseReadiness = {},
  round,
  user,
  sseData,
  betreadyPhaseSignal = 0,
  timeSignalIntervalMs = null,
}) {
  const initialServerPhase = round?.status !== undefined && round?.status !== null
    ? getServerPhaseFromStatus(round.status)
    : SERVER_PHASE.BETREADY;
  const [backendServerPhase, setBackendServerPhase] = useState(initialServerPhase);
  const [serverPhase, setServerPhase] = useState(initialServerPhase);
  const [timePhase, setTimePhase] = useState(() => getTimePhase(round));
  const [timeSignalNow, setTimeSignalNow] = useState(() => new Date());
  const isPhaseReady = (phase) => phaseReadiness[phase] ?? true;


  useEffect(() => {
    if (round?.status === undefined || round?.status === null) {
      return;
    }

    const nextServerPhase = getServerPhaseFromStatus(round.status);
    setBackendServerPhase(nextServerPhase);
    if (isPhaseReady(nextServerPhase)) {
      setServerPhase(nextServerPhase);
    }
  }, [round?.status]);

  useEffect(() => {
    const nextServerPhase = getServerPhaseFromSseData(sseData);
    if (nextServerPhase) {
      setBackendServerPhase(nextServerPhase);
      if (isPhaseReady(nextServerPhase)) {
        setServerPhase(nextServerPhase);
      }
    }
  }, [sseData]);

  useEffect(() => {
    if (isPhaseReady(backendServerPhase)) {
      setServerPhase(backendServerPhase);
    }
  }, [
    backendServerPhase,
    phaseReadiness[SERVER_PHASE.CONFIRMED],
    phaseReadiness[SERVER_PHASE.COMPLETED],
  ]);

  useEffect(() => {
    if (betreadyPhaseSignal > 0) {
      setBackendServerPhase(SERVER_PHASE.BETREADY);
      setServerPhase(SERVER_PHASE.BETREADY);
    }
  }, [betreadyPhaseSignal]);

  useEffect(() => {
    const nextTimePhase = getTimePhase(round);
    setTimePhase((currentTimePhase) => (
      currentTimePhase === nextTimePhase ? currentTimePhase : nextTimePhase
    ));

    const nextDelayMs = getNextTimePhaseChangeDelayMs(round);
    if (nextDelayMs === null) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setTimePhase((currentTimePhase) => {
        const boundaryTimePhase = getTimePhase(round);
        return currentTimePhase === boundaryTimePhase ? currentTimePhase : boundaryTimePhase;
      });
    }, nextDelayMs);

    return () => clearTimeout(timeoutId);
  }, [round?.id, round?.number, round?.starttime, round?.fasttime, round?.endtime, timePhase]);

  useEffect(() => {
    setTimeSignalNow(new Date());

    if (!timeSignalIntervalMs) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setTimeSignalNow(new Date());
    }, timeSignalIntervalMs);

    return () => clearInterval(intervalId);
  }, [round?.id, round?.number, round?.starttime, round?.fasttime, round?.endtime, timeSignalIntervalMs]);

  const timeSignals = useMemo(() => getTimeSignals(round, timeSignalNow), [
    round?.starttime,
    round?.fasttime,
    round?.endtime,
    round?.id,
    round?.number,
    timePhase,
    timeSignalNow,
  ]);

  const roomDisplayPhase = useMemo(
    () => getRoomDisplayPhase({
      serverPhase,
      timePhase,
      event,
      round,
      user,
    }),
    [event, round, serverPhase, timePhase, user],
  );

  return {
    backendServerPhase,
    serverPhase,
    timePhase,
    timeSignals,
    roomDisplayPhase,
  };
}

export default useRoomPhase;
